<template>
  <form-section :title="tr('Payment')" :description="blockDescription">
    <div v-if="availablePackages.length === 0" class="flex items-center gap-2 text-sm text-muted-foreground">
      <loader-circle-icon class="size-4 animate-spin" />
      {{ tr('Loading...') }}
    </div>

    <template v-else>
      <div class="flex flex-col gap-1.5">
        <ui-label>{{ tr('Billing period') }}</ui-label>
        <ui-radio-group v-model="billingPeriod" :disabled="billingPeriodSelectorDisabled" class="flex gap-6">
          <label class="flex cursor-pointer items-center gap-2 text-sm text-foreground">
            <ui-radio-group-item value="monthly" />
            {{ tr('Monthly') }}
          </label>
          <label class="flex cursor-pointer items-center gap-2 text-sm text-foreground">
            <ui-radio-group-item value="annual" />
            {{ tr('Annual') }}
          </label>
        </ui-radio-group>
      </div>

      <div class="flex flex-col gap-1.5">
        <ui-label>{{ tr('Subscription package') }}</ui-label>
        <ui-select v-model="selectedPackageId">
          <ui-select-trigger class="w-full" :aria-label="tr('Subscription package')">
            <ui-select-value :placeholder="tr('Choose a subscription plan')" />
          </ui-select-trigger>
          <ui-select-content>
            <ui-select-item v-for="item in selectablePackages" :key="item.id" :value="String(item.id)">
              {{ formatPackageOptionLabel(item) }}
            </ui-select-item>
          </ui-select-content>
        </ui-select>
      </div>

      <div v-if="featureListIsVisible" class="flex flex-col gap-1.5">
        <ui-label>{{ tr('Managed Account Service') }}</ui-label>

        <div v-if="featureManagedAccountIsAvailable" class="flex flex-col gap-3">
          <p class="m-0 text-xs text-muted-foreground">
            {{ tr('Get up to 1 hour of expert assistance per month (in two 30-minute sessions) to optimize your :app campaigns and maximize your advertising success.') }}
            <template v-if="selectedPackage">
              {{ tr('Price per brand:') }} {{ formatPrice(selectedPackage.feature_managed_account_price, '€', selectedPackage.period) }}
            </template>
          </p>

          <brand-filter
            :model-value="null"
            :all-option="false"
            :allow-inactive="false"
            :exclude-ids="selectedManagedBrandIds"
            :placeholder="tr('Add a brand to manage...')"
            :disabled="!selectedPackage"
            @update:model-value="(brandId) => addManagedBrand(brandId)"
          />

          <div v-if="selectedManagedBrandIds.length > 0" class="flex flex-col divide-y divide-border rounded-lg border border-border">
            <div
              v-for="brandId in selectedManagedBrandIds"
              :key="'managed-brand--' + brandId"
              class="flex items-center justify-between gap-3 px-3 py-2"
            >
              <social-page :source-profiles="getGroupedSourceProfilesByBrandId(brandId)" />
              <ui-button
                variant="ghost"
                size="icon"
                :aria-label="tr('Remove')"
                :disabled="managedAccountReduceDisabled"
                @click="() => removeManagedBrand(brandId)"
              >
                <x-icon />
              </ui-button>
            </div>
          </div>
        </div>
      </div>

      <ui-alert v-if="showManagedAccountContactMessage">
        <info-icon />
        <ui-alert-description>
          {{ tr('Our partner will soon contact you regarding the Managed Account service you requested.') }}
        </ui-alert-description>
      </ui-alert>

      <ui-alert v-if="managedAccountReduceDisabled">
        <info-icon />
        <ui-alert-description>
          {{ tr('The "Managed Account Service" can be canceled no earlier than :date', { ':date': managedAccountCancellableDate }) }}
        </ui-alert-description>
      </ui-alert>
      <ui-alert v-else-if="showManagedAccountCancelInfoBefore">
        <info-icon />
        <ui-alert-description>
          {{ tr('When requesting the "Managed Account Service", the first fee will be determined proportionally according to your billing cycle, but you will not have the opportunity to cancel the service before the next billing. If you request it now, the first opportunity to cancel the service will be on :date', { ':date': managedAccountCancellableDate }) }}
        </ui-alert-description>
      </ui-alert>

      <div class="flex flex-col gap-1.5">
        <ui-label for="subscription-coupon">{{ tr('Coupon code') }}</ui-label>
        <div class="relative">
          <ui-input
            id="subscription-coupon"
            v-model="couponCode"
            :disabled="hadPreviousSubscription"
            :aria-invalid="(!couponValidation.isValid && !!couponValidation.message) || null"
            class="pr-9"
          />
          <loader-circle-icon v-if="couponValidation.isLoading" class="absolute top-1/2 right-3 size-4 -translate-y-1/2 animate-spin text-primary" />
        </div>

        <p v-if="hadPreviousSubscription && !couponCode" class="m-0 text-xs text-muted-foreground">
          {{ tr('You can only use a coupon code on your first subscription') }}
        </p>
        <template v-else-if="couponValidation.isValid">
          <p v-if="couponValidation.discount > 0" class="m-0 text-xs font-semibold text-success">
            {{ tr('This coupon provides :discount% discount on the base subscription and extra brands price', { ':discount': couponValidation.discount }) }}
          </p>
          <p v-if="couponValidation.managed_account_discount > 0" class="m-0 text-xs font-semibold text-success">
            {{ tr('This coupon provides :discount% discount on managed account service', { ':discount': couponValidation.managed_account_discount }) }}
          </p>
        </template>
        <p v-else-if="couponValidation.error" class="m-0 text-xs font-semibold text-destructive">
          {{ couponValidation.error }}
        </p>
      </div>

      <div v-if="showCostBreakdown || costIsLoading" class="flex flex-col rounded-lg border border-border bg-background/40 p-4">
        <div v-if="!costIsLoading" class="flex flex-col divide-y divide-border">
          <div class="flex items-start justify-between gap-4 py-2 text-sm">
            <span class="text-foreground">
              <span class="inline-flex items-center gap-1">
                {{ tr('Subscription base fee') }}
                <info-popup v-if="subscriptionBaseFeeInfoText">
                  <!-- Translations with markup (<b>, <br/>) and numbers from the API. -->
                  <span v-html="subscriptionBaseFeeInfoText" />
                </info-popup>
              </span>
              <small class="block text-xs text-muted-foreground">({{ tr('up to :count brands', { ':count': selectedPackage.max_brands }) }})</small>
            </span>
            <span class="shrink-0 font-medium text-foreground tabular-nums">
              {{ formatPrice(costBreakdown.base_fee_net, '€', selectedPackage.period) }}
            </span>
          </div>

          <div v-if="costBreakdown.extra_brand_fee_net > 0" class="flex items-start justify-between gap-4 py-2 text-sm">
            <span class="text-foreground">
              {{ tr('Extra brand') }} x {{ costBreakdown.extra_brand_count }}
              <small class="block text-xs text-muted-foreground">({{ costBreakdown.extra_brand_count }} x {{ formatPrice(costBreakdown.extra_brand_unit_net, '€', selectedPackage.period) }})</small>
            </span>
            <span class="shrink-0 font-medium text-foreground tabular-nums">
              {{ formatPrice(costBreakdown.extra_brand_fee_net, '€', selectedPackage.period) }}
            </span>
          </div>

          <div v-if="costBreakdown.managed_account_fee_net > 0" class="flex items-start justify-between gap-4 py-2 text-sm">
            <span class="text-foreground">
              {{ tr('Managed Account Service') }} x {{ costBreakdown.managed_account_slot_count }}
              <small class="block text-xs text-muted-foreground">({{ costBreakdown.managed_account_slot_count }} x {{ formatPrice(costBreakdown.managed_account_unit_net, '€', selectedPackage.period) }})</small>
            </span>
            <span class="shrink-0 font-medium text-foreground tabular-nums">
              {{ formatPrice(costBreakdown.managed_account_fee_net, '€', selectedPackage.period) }}
            </span>
          </div>

          <div v-if="costBreakdown.coupon_discount_net < 0" class="flex items-start justify-between gap-4 py-2 text-sm">
            <span class="text-success">{{ tr('Coupon discount') }}</span>
            <span class="shrink-0 font-medium text-success tabular-nums">
              - {{ formatPrice(costBreakdown.coupon_discount_net * -1, '€', selectedPackage.period) }}
            </span>
          </div>
        </div>

        <div :class="[ 'flex items-start justify-between gap-4', !costIsLoading && 'mt-2 border-t border-border pt-3' ]">
          <span class="text-sm font-semibold text-foreground">{{ tr('Cost') }}</span>
          <loader-circle-icon v-if="costIsLoading" class="size-4 animate-spin text-primary" />
          <span v-else class="flex flex-col items-end text-right">
            <span class="text-lg font-bold text-foreground tabular-nums">
              {{ formatPrice(vatRate ? totalCost * (1 + vatRate) : totalCost, '€', selectedPackage.period) }}
            </span>
            <small v-if="vatRate && totalCost > 0" class="block text-xs text-muted-foreground">
              ({{ tr('net') + ' ' + formatPrice(totalCost, '€') }} + {{ vatRate * 100 }}% {{ tr('VAT') }})
            </small>
          </span>
        </div>

        <div v-if="!costIsLoading && payableNow" class="mt-2 flex items-start justify-between gap-4">
          <span class="text-sm text-foreground">{{ tr('Payable now') }}:</span>
          <span class="flex flex-col items-end text-right">
            <span class="text-sm font-semibold text-foreground tabular-nums">
              {{ formatPrice(vatRate ? payableNow * (1 + vatRate) : payableNow, '€') }}
            </span>
            <small v-if="vatRate" class="block text-xs text-muted-foreground">
              ({{ tr('net') + ' ' + formatPrice(payableNow, '€') }} + {{ vatRate * 100 }}% {{ tr('VAT') }})
            </small>
          </span>
        </div>
      </div>
    </template>
  </form-section>
</template>

<script>
import { Info, LoaderCircle, X } from '@lucide/vue';

import PaymentHistory from '@/entities/PaymentHistory';
import PaymentHistoryService from '@/services/PaymentHistory';
import SubscriptionService from '@/services/Subscription';
import SubscriptionCouponService from '@/services/SubscriptionCoupon';
import BrandFilter from '@/ui/components/BrandFilter.vue';
import FormSection from '@/ui/components/FormSection.vue';
import InfoPopup from '@/ui/components/InfoPopup.vue';
import PaymentModal from '@/ui/components/PaymentModal.vue';
import SocialPage from '@/ui/components/SocialPage.vue';
import { formatToStringMD, modifyDate } from '@/utils/date.js';
import debounce from '@/utils/debounce.js';
import { formatPrice } from '@/utils/number.js';
import { clearCouponCode, getCouponCode } from '@/utils/storage.js';
import { formatPcs } from '@/utils/string.js';

export default {
  name: 'ChangeSubscriptionForm',
  components: {
    BrandFilter,
    FormSection,
    InfoIcon: Info,
    InfoPopup,
    LoaderCircleIcon: LoaderCircle,
    SocialPage,
    XIcon: X,
  },
  props: {
    billingCountry: {
      type: String,
      required: false,
      default: () => null,
    },
    vatNumber: {
      type: String,
      required: false,
      default: () => null,
    },
  },
  data() {
    return {
      isLoading: false,
      billingPeriod: 'monthly',
      selectedPackage: null,
      selectedManagedBrandIds: [],
      availablePackages: [],

      costUpdater: debounce(() => this.updateCost(), 800),
      costIsLoading: false,
      totalCost: null,
      payableNow: null,
      costBreakdown: null,

      originalValues: {},
      hadPreviousSubscription: false,
      couponCode: '',
      couponValidation: {
        isLoading: false,
        isValid: false,
        discount: null,
        managed_account_discount: null,
        error: null,
      },
      validateCouponDebounced: debounce(() => this.validateCoupon(), 800),
    };
  },
  computed: {
    company() {
      return this.$root.currentCompany;
    },
    computedBillingCountry() {
      return this.billingCountry ?? this.company.billing_country;
    },
    computedVatNumber() {
      return this.vatNumber ?? this.company.billing_vat_number;
    },
    vatRate() {
      if (!this.computedBillingCountry) {
        return 0;
      }

      if (this.computedBillingCountry === 'HU' || !this.computedVatNumber) {
        return 0.27;
      }

      return 0;
    },
    blockDescription() {
      if (!this.company.paid_subscription?.package) {
        return null;
      }

      return this.tr('In case of switching to a smaller subscriber package or canceling services, you can use the service ' +
          'you have already paid for until the end of the current billing period.') +
        '<br>' +
        this.tr('In the event of switching to a higher subscription package or requesting new services, we will calculate ' +
          'a fee based on your current billing period and you can start using it immediately.');
    },
    // The kit select works with string values.
    selectedPackageId: {
      get() {
        return this.selectedPackage ? String(this.selectedPackage.id) : undefined;
      },
      set(id) {
        this.selectedPackage = this.selectablePackages.find(item => String(item.id) === id) ?? null;
      },
    },
    selectablePackages() {
      return this.availablePackages.filter(item => item.period === this.billingPeriod);
    },
    billingPeriodSelectorDisabled() {
      const current = this.company.current_subscription?.package;
      const paid = this.company.paid_subscription?.package;

      if (paid) {
        return true;
      }

      return !current || current.machine_name.indexOf('free_trial') === -1;
    },
    featureManagedAccountIsAvailable() {
      return this.selectedPackage?.feature_managed_account_price !== null;
    },
    featureListIsVisible() {
      if (!this.selectedPackage) {
        return true;
      }

      return this.featureManagedAccountIsAvailable;
    },
    sourceProfiles() {
      return this.company?.source_profiles ?? [];
    },
    managedAccountReduceDisabled() {
      const currentSlotCount = this.company.current_subscription?.feature_managed_account ?? 0;
      if (currentSlotCount === 0) {
        return false;
      }
      return this.company.feature_managed_account_cancellable_at > new Date()
        && this.selectedManagedBrandIds.length <= currentSlotCount;
    },
    showManagedAccountCancelInfoBefore() {
      if (!this.company.current_subscription?.package || this.company.current_subscription?.package.is_free_trial) {
        return false;
      }

      const currentSlotCount = this.company.current_subscription?.feature_managed_account ?? 0;
      return this.selectedManagedBrandIds.length > 0 && currentSlotCount === 0;
    },
    managedAccountCancellableDate() {
      if (this.company.feature_managed_account_cancellable_at) {
        return formatToStringMD(this.company.feature_managed_account_cancellable_at, this.tr);
      }

      const date = modifyDate(this.company.next_payment_date, '+1 day');

      return formatToStringMD(date, this.tr);
    },
    showManagedAccountContactMessage() {
      return !this.company.has_manager_company
        && ((this.company.paid_subscription?.feature_managed_account ?? 0) > 0 || (this.company.current_subscription?.feature_managed_account ?? 0) > 0);
    },
    buttonLabel() {
      if (this.company.current_subscription?.package !== null && !this.company.current_subscription?.package.is_free_trial) {
        return this.tr('Change subscription');
      }

      return this.tr('Choose subscription package');
    },
    creatorCreditInfo() {
      const credits = this.selectedPackage ? this.selectedPackage.creator_credits : 0;
      if (!credits) {
        return null;
      }

      return this.tr(
        'With the selected subscription package, you receive <b>:count credits</b> :period to use Infinite∞Creator Lite.',
        {
          ':count': credits,
          ':period': this.selectedPackage
            ? (this.selectedPackage.period === 'monthly' ? this.tr('each month') : this.tr('each year'))
            : '',
        },
      );
    },
    packageBrandPricingInfoText() {
      if (!this.selectedPackage || this.selectedPackage.max_brands === null) {
        return null;
      }

      const parts = [
        this.tr(
          this.selectedPackage.max_brands === 1
            ? 'The base fee is :price and allows using up to :max_brands brand.'
            : 'The base fee is :price and allows using up to :max_brands brands.',
          {
            ':price': this.formatPrice(this.selectedPackage.price, '€', this.selectedPackage.period),
            ':max_brands': this.selectedPackage.max_brands,
          },
        ),
      ];

      if (this.selectedPackage.extra_brand_price !== null) {
        parts.push(this.tr('Each additional brand costs :price.', {
          ':price': this.formatPrice(this.selectedPackage.extra_brand_price, '€', this.selectedPackage.period),
        }));
      }

      return parts.join(' ');
    },
    subscriptionBaseFeeInfoText() {
      const parts = [];
      if (this.packageBrandPricingInfoText) {
        parts.push(this.packageBrandPricingInfoText);
      }
      if (this.creatorCreditInfo) {
        parts.push(this.creatorCreditInfo);
      }

      if (parts.length === 0) {
        return null;
      }

      return parts.join('<br/>');
    },
    showCostBreakdown() {
      if (!this.selectedPackage || !this.costBreakdown) {
        return false;
      }

      return this.totalCost > 0 || this.costBreakdown.coupon_discount_net < 0;
    },
  },
  watch: {
    billingPeriod() {
      if (this.availablePackages.length === 0) {
        return;
      }

      this.selectedPackage = null;
    },
    selectedPackage() {
      if (this.availablePackages.length === 0) {
        return;
      }

      this.costUpdater();
    },
    selectedManagedBrandIds: {
      deep: true,
      handler() {
        if (this.availablePackages.length === 0) {
          return;
        }
        if (!this.selectedPackage) {
          return;
        }

        this.costUpdater();
      },
    },
    featureManagedAccountIsAvailable(val) {
      if (!val) {
        this.selectedManagedBrandIds = [];
      }
    },
    couponCode() {
      if (this.hadPreviousSubscription) {
        return;
      }
      this.costUpdater();
      this.validateCouponDebounced();
    },
  },
  created() {
    if (this.company.managed_brand_ids?.length > 0) {
      this.selectedManagedBrandIds = [ ...this.company.managed_brand_ids ];
    }

    SubscriptionService.getPackages()
      .then(resp => {
        let searchablePackageId;
        if (this.company.paid_subscription?.package) {
          searchablePackageId = this.company.paid_subscription.package.id;
        }

        if (searchablePackageId) {
          this.selectedPackage = resp.find(item => item.id === searchablePackageId);
          if (this.selectedPackage) {
            this.billingPeriod = this.selectedPackage.period;
          }
        }

        this.originalValues.billingPeriod = this.billingPeriod ?? null;
        this.originalValues.selectedPackage = this.selectedPackage ?? null;
        this.originalValues.selectedManagedBrandIds = [ ...this.selectedManagedBrandIds ];

        this.updateCost();

        this.$nextTick(() => this.availablePackages = resp);

        return resp;
      })
      .catch(() => this.error('Failed to load subscription packages'));

    this.hadPreviousSubscription = this.company.paid_subscription !== null || (this.company.current_subscription !== null && !this.company.current_subscription.package?.is_free_trial);
    if (!this.hadPreviousSubscription) {
      SubscriptionService.hadPreviousSubscription()
        .then(resp => {
          this.hadPreviousSubscription = resp;

          const storedCouponCode = getCouponCode();
          if (storedCouponCode) {
            this.couponCode = storedCouponCode;
            this.validateCoupon();
          }

          return resp;
        })
        .catch(() => {
          this.error('Failed to check previous subscription');
          this.hadPreviousSubscription = true;
        });
    }

    if (this.company.paid_subscription?.coupon?.code) {
      const coupon = this.company.paid_subscription.coupon;

      this.couponValidation = {
        isValid: true,
        discount: coupon.discount,
        managed_account_discount: coupon.managed_account_discount,
        isLoading: false,
      };
      this.couponCode = this.company.paid_subscription.coupon.code;
    }
  },
  methods: {
    formatPrice,
    formatPcs,
    getPackagePeriodLabel(subscriptionPackage) {
      return this.tr(subscriptionPackage.period === 'monthly' ? 'month' : 'year');
    },
    formatPackageOptionLabel(subscriptionPackage) {
      if (
        subscriptionPackage.price !== null
        && subscriptionPackage.extra_brand_price !== null
        && Number(subscriptionPackage.price) === Number(subscriptionPackage.extra_brand_price)
      ) {
        return subscriptionPackage.name
          + ' - '
          + this.formatPrice(subscriptionPackage.price, '€')
          + ' / '
          + this.getPackagePeriodLabel(subscriptionPackage)
          + ' / '
          + this.tr('brand');
      }

      const brandPricingInfo = this.getPackageBrandPricingInfoLabel(subscriptionPackage);
      const baseLabel = subscriptionPackage.name + ' - ' + this.formatPrice(subscriptionPackage.price, '€', subscriptionPackage.period);

      if (!brandPricingInfo) {
        return baseLabel;
      }

      return baseLabel + ' (' + brandPricingInfo + ')';
    },
    getPackageBrandPricingInfoLabel(subscriptionPackage) {
      const parts = [];

      if (subscriptionPackage.max_brands !== null) {
        parts.push(this.tr(
          subscriptionPackage.max_brands === 1
            ? 'contains :count brand'
            : 'contains :count brands',
          { ':count': subscriptionPackage.max_brands },
        ));
      }

      if (subscriptionPackage.extra_brand_price !== null) {
        parts.push(this.tr('+:price / extra brand', {
          ':price': this.formatPrice(subscriptionPackage.extra_brand_price, '€', subscriptionPackage.period),
        }));
      }

      return parts.join(', ');
    },
    updateCost() {
      if (!this.selectedPackage) {
        return;
      }

      this.costIsLoading = true;

      SubscriptionService.calculateCost({
        packageId: this.selectedPackage.id,
        managedAccountBrandIds: this.selectedManagedBrandIds,
        couponCode: this.couponCode,
      })
        .then(resp => {
          this.totalCost = resp.total_cost;
          this.payableNow = resp.payable_now;
          this.costBreakdown = resp.cost_breakdown ?? null;

          return resp;
        })
        .catch(err => {
          this.costBreakdown = null;
          this.error(err);
        })
        .finally(() => this.costIsLoading = false);
    },
    async validateCoupon() {
      if (!this.couponCode) {
        this.couponValidation = {
          isLoading: false,
          isValid: false,
          discount: null,
          managed_account_discount: null,
          error: null,
        };
        return;
      }

      this.couponValidation.isLoading = true;

      let response = null;
      try {
        response = await SubscriptionCouponService.validateCoupon(this.couponCode);
      }
      catch (err) {
        this.couponValidation = {
          isLoading: false,
          isValid: false,
          discount: null,
          managed_account_discount: null,
          error: err.message,
        };
        return;
      }

      if (response.error) {
        this.couponValidation = {
          isLoading: false,
          isValid: false,
          discount: null,
          managed_account_discount: null,
          error: response.error,
        };
        return;
      }
      this.couponValidation = {
        isLoading: false,
        isValid: true,
        discount: response.discount,
        managed_account_discount: response.managed_account_discount,
        error: null,
      };
    },
    async save() {
      if (this.isLoading) {
        return;
      }

      if (!this.selectedPackage) {
        return;
      }

      this.isLoading = true;

      try {
        const resp = await SubscriptionService.change({
          packageId: this.selectedPackage.id,
          managedAccountBrandIds: this.selectedManagedBrandIds,
          couponCode: this.couponCode,
        });

        if (resp.action_required === 'open_payment') {
          this.infoToast(this.tr('Automatic payment failed.') + ' ' +
            resp.message + ' ' +
            this.tr('The use of a payment form is required.'));

          const paymentHistory = new PaymentHistory(resp.payment_history);
          this.$fullscreenModal.open(
            this,
            PaymentModal,
            {
              paymentHistory,
              onSuccess: () => setTimeout(() => window.location.reload(), 500),
            },
            { class: '' },
          );
          this.isLoading = false;
          return;
        }

        if (resp.payment_history) {
          const paymentHistory = resp.payment_history;
          await this.checkPaymentHistoryStatus(paymentHistory.id);
        }

        this.$toast.clear();
        this.$toast.success(this.tr('Your subscription has been successfully modified.'));

        this.$root.fetchUserData();
        this.updateCost();

        clearCouponCode();
      }
      catch (err) {
        this.error(err);
      }

      this.isLoading = false;
    },
    checkPaymentHistoryStatus(id, originalResolver, originalRejecter) {
      return new Promise((resolve, reject) => {
        if (!originalResolver) {
          originalResolver = resolve;
        }
        if (!originalRejecter) {
          originalRejecter = reject;
        }

        PaymentHistoryService.get(id)
          .then(entity => {
            if (entity.status === 'success' || entity.status === 'success_wo_payment') {
              const currentActivePackage = this.$root.currentCompany.current_subscription?.package;
              if (
                !currentActivePackage
                || currentActivePackage.machine_name.indexOf('free_trial') !== -1
              ) {
                if (window.fbq) {
                  window.fbq('trackCustom', 'SubscriptionStarted');
                }
                if (window.ttq) {
                  window.ttq.track('SubscriptionStarted');
                  window.ttq.track('Subscribe');
                }
                if (window.rdt) {
                  window.rdt('track', 'Custom', { customEventName: 'SubscriptionStarted' });
                }
              }

              originalResolver();
              return entity;
            }
            if (entity.status === 'failed') {
              originalRejecter(new Error(entity.fail_reason));
              return entity;
            }

            setTimeout(() => this.checkPaymentHistoryStatus(id, originalResolver, originalRejecter), 2000);
            return entity;
          })
          .catch(err => reject(err));
      });
    },
    getGroupedSourceProfilesByBrandId(brandId) {
      const brand = this.$root.currentCompany.brands.find(b => b.id === brandId);
      if (!brand) {
        // eslint-disable-next-line no-console
        console.error('Brand not found', brandId);
        return [];
      }

      let sourceProfiles = [
        brand.facebook_source_profile,
        brand.instagram_source_profile,
      ];
      sourceProfiles = sourceProfiles.filter(sp => !!sp);

      return sourceProfiles;
    },
    addManagedBrand(brandId) {
      if (!brandId || this.selectedManagedBrandIds.includes(brandId)) {
        return;
      }
      this.selectedManagedBrandIds = [ ...this.selectedManagedBrandIds, brandId ];
    },
    removeManagedBrand(brandId) {
      this.selectedManagedBrandIds = this.selectedManagedBrandIds.filter(id => id !== brandId);
    },
    isChanged() {
      if (
        'billingPeriod' in this.originalValues
        && JSON.stringify(this.billingPeriod) != JSON.stringify(this.originalValues.billingPeriod)
      ) {
        return true;
      }
      if (
        'selectedPackage' in this.originalValues
        && JSON.stringify(this.selectedPackage) != JSON.stringify(this.originalValues.selectedPackage)
      ) {
        return true;
      }
      if (
        'selectedManagedBrandIds' in this.originalValues
        && JSON.stringify(this.selectedManagedBrandIds) != JSON.stringify(this.originalValues.selectedManagedBrandIds)
      ) {
        return true;
      }

      return false;
    },
  },
};
</script>
